import React, { useEffect, useRef, useState, useCallback } from "react";
import socketIOClient from "socket.io-client";
import JSZip from "jszip";
import CustomHeader from "../../components/header/index.jsx";
import PageHeader from "../../components/pageHeader";
import whiteLogo from "../../assets/icons/white_logo_png.png";
import url from "../../config.js";
import DeploymentAgentConsole from "./DeploymentAgentConsole";
import DeploymentValidationPanel from "./DeploymentValidationPanel";
import DeploymentResult from "./DeploymentResult";
import RepoBranchRows from "../smartHubNew/RepoBranchRows";
import { analyzeProject, listRepositories } from "../smartHubNew/repoApi";
import { buildV3Config, deployEndpoint, deployBodyFields } from "./buildDeployConfig";
import "./deploymentagent.scss";

// Deployment Agent — V3 contract (FRONTEND_DEPLOYMENT_V3.md): analyze → (per-service
// repo+branch) → deploy. Stateless: deploy resends the same `source`; a
// deployment_validation_pending is answered by RESENDING the same request + answers.
const backendUrl = url.backendUrl;
const domain = url.domain;
const token = () => localStorage.getItem("token") || "";
const nowTime = () => new Date().toLocaleTimeString("en-US", { hour12: false });
const genSessionId = () => `deploy-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const TECHS = [
  { key: "mule", label: "MuleSoft" },
  { key: "springboot", label: "Spring Boot" },
  { key: "dotnet", label: ".NET" },
];
// The CI run stage is labeled by the chosen platform (GitHub Actions vs Azure Pipeline),
// not hardcoded — a github-actions deploy must not show an "Azure Pipeline" stage.
const ciStageLabel = (platform) => (platform === "azure-devops" ? "Azure Pipeline" : "GitHub Actions");
const stageMapFor = (tech, platform) => {
  const ci = ciStageLabel(platform);
  return tech === "mule" ? ["Anypoint Exchange", "GitHub", ci] : ["GitHub", ci];
};
const seedStages = (tech, platform) => stageMapFor(tech, platform).map((n, i) => ({ name: n, status: i === 0 ? "active" : "pending" }));
// Push/repo stages vs the CI run stage, by stage name.
const isCiStage = (name) => /pipeline|actions/i.test(name);
const isPushStage = (name) => /anypoint|github|azure repos|repo/i.test(name) && !isCiStage(name);

// Deploy error code → user message (FRONTEND_DEPLOYMENT_V3.md §5).
const DEPLOY_ERROR_MESSAGES = {
  VALIDATION_FAILED: "Please check the highlighted field.",
  BRANCH_REQUIRED: "A branch is required for a repository that already has content. Nothing was written.",
  BASE_BRANCH_NOT_FOUND: "That base branch doesn't exist in the repository. Nothing was written.",
  MISSING_TARGETS: "Pick a repository for every service.",
  BRANCH_MOVED: "The branch moved after it was read (someone pushed) — nothing was pushed. Deploy again.",
  BRANCH_CONFLICT: "The branch changed during the push — deploy again.",
  TECHNOLOGY_MISMATCH: "This project doesn't match the selected technology — pick the right one.",
  INVALID_LAYOUT: "That layout isn't allowed for this project.",
  UNSUPPORTED_PROJECT: "This doesn't look like a deployable project.",
  AUTH_FAILED: "The token/PAT was rejected — re-link the account in Connected Apps.",
  PERMISSION_DENIED: "Permission denied for that account.",
};

// Read the repositories saved by the Save-to-repository flow (SmartHub persists them in
// localStorage) so the Deployment Agent can default to redeploying those branches.
const readSavedRepos = () => {
  try {
    const raw = localStorage.getItem("user");
    const uid = raw ? (JSON.parse(raw).id || JSON.parse(raw)._id) : null;
    if (!uid) return null;
    const st = localStorage.getItem(`smarthub_state_${uid}`);
    if (!st) return null;
    const saved = JSON.parse(st).savedRepoResult;
    const list = (saved && (saved.repositories || saved)) || null;
    return Array.isArray(list) && list.length ? list : null;
  } catch (_) { return null; }
};

function setDeep(obj, dotted, value) {
  const parts = String(dotted || "").split(".");
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i++) { cur[parts[i]] = cur[parts[i]] || {}; cur = cur[parts[i]]; }
  cur[parts[parts.length - 1]] = value;
  return obj;
}

// Module-scope so their component identity is stable across renders (defining them
// inside the component remounts the <select>s every render, detaching the change event).
const Dropdown = ({ label, list, value, onChange, render }) => (
  <label className="da-field"><span>{label}</span>
    <select className="da-input" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">— none —</option>
      {list.map((a, i) => <option key={i} value={i}>{render(a, i)}</option>)}
    </select>
  </label>
);

const KeyValRows = ({ label, rows, setRows, secret }) => (
  <div style={{ marginBottom: 8 }}>
    <div style={{ fontSize: 11, color: "#7a93bc", margin: "4px 0" }}>{label}</div>
    {rows.map((r, i) => (
      <div className="da-row" key={i} style={{ marginBottom: 4 }}>
        <input className="da-input" placeholder="name" value={r.k} onChange={(e) => { const val = e.target.value; setRows((p) => p.map((x, j) => (j === i ? { ...x, k: val } : x))); }} />
        <input className="da-input" type={secret ? "password" : "text"} placeholder="value" value={r.v} onChange={(e) => { const val = e.target.value; setRows((p) => p.map((x, j) => (j === i ? { ...x, v: val } : x))); }} />
        <button className="da-tab" onClick={() => setRows((p) => p.filter((_, j) => j !== i))}>✕</button>
      </div>
    ))}
    <button className="da-tab" onClick={() => setRows((p) => [...p, { k: "", v: "" }])}>+ Add</button>
  </div>
);

export default function DeploymentAgent() {
  const savedRepos = readSavedRepos();
  const [phase, setPhase] = useState("setup"); // setup | validation | deploying
  const [tech, setTech] = useState("springboot");
  const [sourceMode, setSourceMode] = useState(savedRepos ? "repository" : "archive"); // archive | path | repository
  const [ciPlatform, setCiPlatform] = useState("github-actions");
  const [started, setStarted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);       // structured deploy result
  const [retryable, setRetryable] = useState(false); // BRANCH_MOVED / BRANCH_CONFLICT → offer "Deploy again"

  // archive / folder
  const [archiveFile, setArchiveFile] = useState(null);
  const [zipping, setZipping] = useState(false);
  const [zipInfo, setZipInfo] = useState(null);
  // project centre
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [projectPath, setProjectPath] = useState("");
  const [projectName, setProjectName] = useState("");
  // repository source (deploy existing branches in place)
  const [srcProvider, setSrcProvider] = useState(savedRepos && savedRepos[0] && savedRepos[0].provider === "azure-repos" ? "azure-repos" : "github");
  const [srcProject, setSrcProject] = useState(""); // Azure DevOps project (azure-repos source)
  const [srcRepos, setSrcRepos] = useState(
    savedRepos
      ? savedRepos.map((r) => ({ service: r.service || "", repo: r.repo || r.fullName || "", branch: r.branch || "" }))
      : [{ service: "", repo: "", branch: "" }]
  );

  // optional pipeline values editor: rows of { k, v } → variables + secrets/secretVariables
  const [showValues, setShowValues] = useState(false);
  const [valVars, setValVars] = useState([]);
  const [valSecrets, setValSecrets] = useState([]);

  // optional: one repository for the whole project (monorepo)
  const [useMono, setUseMono] = useState(false);
  const [mono, setMono] = useState({ repo: "", branch: "", baseBranch: "" });

  const [sessionId, setSessionId] = useState(genSessionId());
  const sessionIdRef = useRef(sessionId);
  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);

  // Connected Apps
  const [muleAccounts, setMuleAccounts] = useState([]);
  const [repoAccounts, setRepoAccounts] = useState([]);
  const [azureAccounts, setAzureAccounts] = useState([]);
  const [vaultAccounts, setVaultAccounts] = useState([]);
  const [environments, setEnvironments] = useState([]);
  const [loadingEnv, setLoadingEnv] = useState(false);
  const [sel, setSel] = useState({ repo: "", azure: "", mule: "", env: "", vault: "" });
  const [nexus, setNexus] = useState({ username: "", password: "" }); // mule EE repository login

  // analyze → services; target repos from RepoBranchRows
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState("");
  const [units, setUnits] = useState(null);     // null = not analyzed yet
  const [targetRepos, setTargetRepos] = useState([]);
  const [targetValid, setTargetValid] = useState(false);
  const [targetProvider, setTargetProvider] = useState("github"); // github | azure-repos (push target for archive/path sources)
  const [targetProject, setTargetProject] = useState(""); // Azure DevOps project for an azure-repos target

  // validation + progress
  const [validation, setValidation] = useState(null);
  const [logs, setLogs] = useState([]);
  const [stages, setStages] = useState([]);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [confirmation, setConfirmation] = useState(null);
  const lastRequestRef = useRef(null); // { config, source, version } to resend on validation

  // chat
  const [messages, setMessages] = useState([]);
  const [thinking, setThinking] = useState(false);
  const chatEndRef = useRef(null);
  const pushBot = (t) => setMessages((m) => [...m, { id: `b${Date.now()}-${m.length}`, role: "bot", text: t }]);
  const pushUser = (t) => setMessages((m) => [...m, { id: `u${Date.now()}-${m.length}`, role: "user", text: t }]);
  const GREETING = "Hi — I'm your Deployment Agent. Pick a source (archive, a saved project, or existing repositories), choose the target repositories/branches and credentials, then Deploy. I'll validate, ask for anything missing, and run the pipeline.";
  useEffect(() => { pushBot(GREETING); }, []); // eslint-disable-line
  useEffect(() => { if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: "smooth" }); }, [messages, thinking, phase]);

  const socketRef = useRef(null);

  const loadResource = useCallback((path, setter, key) => {
    return fetch(`${domain}/api/v1/${path}`, { headers: { "x-access-token": token() } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        let arr = [];
        if (Array.isArray(d)) arr = d;
        else if (d && typeof d === "object") {
          if (key && Array.isArray(d[key])) arr = d[key];
          else if (Array.isArray(d.data)) arr = d.data;
          else if (Array.isArray(d[path])) arr = d[path];
          else { const f = Object.values(d).find(Array.isArray); if (f) arr = f; }
        }
        setter(arr);
      })
      .catch(() => setter([]));
  }, []);

  useEffect(() => {
    loadResource("mule", setMuleAccounts, "mule");
    loadResource("repo", setRepoAccounts);
    loadResource("azaccount", setAzureAccounts);
    loadResource("azurevault", setVaultAccounts);
    fetch(`${domain}/api/v1/project`, { headers: { "x-access-token": token() } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setProjects(Array.isArray(d && d.projects)
        ? d.projects.filter((p) => p && p.projectPath && Array.isArray(p.generatedApis) && p.generatedApis.length > 0 && String(p.technology || p.projectType || "").toLowerCase() !== "design")
        : []))
      .catch(() => setProjects([]));
  }, [loadResource]);

  const fetchEnvironments = useCallback((account) => {
    setEnvironments([]); setSel((s) => ({ ...s, env: "" }));
    if (!account) return;
    setLoadingEnv(true);
    fetch(`${domain}/api/v1/mule/org/environments`, {
      method: "POST", headers: { "Content-Type": "application/json", "x-access-token": token() },
      body: JSON.stringify({ muleOrgId: account.muleOrgId, connAppClientId: account.connAppClientId, connAppClientSecret: account.connAppClientSecret }),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { const list = (d && (d.data || d.environments)) || []; setEnvironments(Array.isArray(list) ? list : []); })
      .catch(() => setEnvironments([]))
      .finally(() => setLoadingEnv(false));
  }, []);

  const zipLocalFolder = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const rootName = ((files[0].webkitRelativePath || files[0].name).split("/")[0]) || "project";
    setError(""); setZipInfo(null); setArchiveFile(null); setZipping(true);
    try {
      const zip = new JSZip();
      for (const f of files) zip.file(f.webkitRelativePath || f.name, await f.arrayBuffer());
      const blob = await zip.generateAsync({ type: "blob" });
      const name = `${rootName}.zip`;
      setArchiveFile(new File([blob], name, { type: "application/zip" }));
      setZipInfo({ name, count: files.length });
      if (!projectName.trim()) setProjectName(rootName);
    } catch (e) { setError("Could not read that folder: " + (e && e.message)); }
    finally { setZipping(false); }
  };

  const advanceDeployStage = (text) => {
    const t = (text || "").toLowerCase();
    setStages((prev) => {
      const idx = prev.findIndex((s) => s.status === "active"); if (idx < 0) return prev;
      const next = prev[idx + 1]; if (!next) return prev;
      const n = next.name.toLowerCase(); let adv = false;
      if (n === "github" && (t.includes("github") || t.includes("git push") || t.includes("pushing"))) adv = true;
      if ((n.includes("azure") || n.includes("pipeline")) && (t.includes("azure") || t.includes("pipeline") || t.includes("trigger") || t.includes("devops"))) adv = true;
      if (!adv) return prev;
      return prev.map((s, i) => (i === idx ? { ...s, status: "done" } : i === idx + 1 ? { ...s, status: "active" } : s));
    });
  };

  const techOf = (p) => {
    const t = String((p && (p.technology || p.projectType)) || "").toLowerCase();
    if (t.includes("spring")) return "springboot";
    if (t.includes("dotnet") || t.includes(".net") || t === "net") return "dotnet";
    if (t.includes("mule")) return "mule";
    return null;
  };

  useEffect(() => {
    const socket = socketIOClient(url.SOCKET, { transports: ["websocket"], reconnection: true, reconnectionAttempts: Infinity, reconnectionDelay: 2000 });
    socketRef.current = socket;
    socket.on("connect", () => socket.emit("join-session", sessionIdRef.current));
    socket.on("deployment log", (d) => {
      if (!d || !d.message) return;
      setLogs((prev) => [...prev, { type: d.type || "info", timestamp: nowTime(), text: d.message }]);
      const msg = String(d.message);
      // Only an explicit error event fails the active stage. Do NOT infer failure from
      // the word "fail"/"error" in a log line — warnings like "jobs will fail unless…"
      // are informational and were wrongly failing the (successful) GitHub push stage.
      // Do NOT mark stages "done" from a log line either: the deploy HTTP call is
      // synchronous (returns only after the run concludes), so setStagesFromResult is the
      // authoritative "done/failed" source. During the run we only advance stage-by-stage
      // (advanceDeployStage) so the CI stage — always last — keeps showing "working…" while
      // pipeline:log streams, instead of flashing all-done then flipping to failed.
      if (d.type === "error") setStages((prev) => prev.map((s) => (s.status === "active" ? { ...s, status: "error" } : s)));
      else advanceDeployStage(msg);
    });
    socket.on("deployment:stages", (d) => { if (d && d.stages) setStages(d.stages); });
    socket.on("repository:log", (d) => { if (d && d.message) setLogs((prev) => [...prev, { type: d.level || "info", timestamp: nowTime(), text: d.message }]); });
    socket.on("pipeline:log", (res) => {
      if (!res || !Array.isArray(res.lines)) return;
      const stageName = res.stage || "Pipeline"; const failed = res.summary === false;
      setPipelineStages((prev) => {
        const next = prev.slice(); const i = next.findIndex((s) => s.stage === stageName);
        const entry = i >= 0 ? { ...next[i] } : { stage: stageName, recordType: res.recordType, lines: [], failed: false };
        entry.lines = failed ? res.lines.slice() : entry.lines.concat(res.lines); entry.failed = entry.failed || failed;
        entry.recordType = res.recordType || entry.recordType; entry.updatedAt = res.timestamp || new Date().toISOString();
        if (i >= 0) next[i] = entry; else next.push(entry); return next;
      });
      // Mirror each pipeline line into the flat live log, tagged with its step — so the
      // console's live-log section shows the step label and the real failure dump (not only
      // the per-stage panel). Matches SmartHub's `[${stageName}] ${line}` behaviour.
      setLogs((prev) => [...prev, ...res.lines.map((line) => ({ type: failed ? "error" : "info", timestamp: nowTime(), text: `[${stageName}] ${line}` }))]);
      // A failed pipeline stage must fail the top-level Azure Pipeline stage (and the
      // overall deployment) — never advance it to "done", or the console reads all-green.
      if (failed) setStages((prev) => prev.map((s) => (isCiStage(s.name) ? { ...s, status: "error" } : s)));
      else advanceDeployStage("azure pipeline " + stageName);
    });
    return () => { try { socket.disconnect(); } catch (_) {} };
  }, []);

  // ── Source object ─────────────────────────────────────────────
  const repoAcc = () => (sel.repo === "" ? null : repoAccounts[Number(sel.repo)]);
  const azAcc = () => (sel.azure === "" ? null : azureAccounts[Number(sel.azure)]);
  const muleAcc = () => (sel.mule === "" ? null : muleAccounts[Number(sel.mule)]);
  const vaultAcc = () => (sel.vault === "" ? null : vaultAccounts[Number(sel.vault)]);
  const ghCreds = () => { const a = repoAcc(); return a ? { owner: a.gitUsername || a.username || "", token: a.gitPat || a.token || "" } : null; };

  // Existing repos of the selected GitHub account, for the source-repository dropdown
  // (repository / run-branch source). Owner-qualified full names so run-branch deploys
  // the exact repo the user picks.
  const [srcGhRepos, setSrcGhRepos] = useState([]);
  useEffect(() => {
    const c = ghCreds();
    if (sourceMode !== "repository" || srcProvider !== "github" || !(c && c.token)) { setSrcGhRepos([]); return; }
    const ctrl = new AbortController();
    fetch("https://api.github.com/user/repos?per_page=100&sort=updated&affiliation=owner,collaborator,organization_member", {
      headers: { Authorization: `Bearer ${c.token}`, Accept: "application/vnd.github+json" }, signal: ctrl.signal,
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => { if (Array.isArray(list)) setSrcGhRepos(list.map((x) => x.full_name || x.name).filter(Boolean)); })
      .catch(() => {});
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceMode, srcProvider, sel.repo]);

  // Existing Azure Repos of the selected Azure DevOps account + project, for the source
  // dropdown. Azure DevOps blocks browser CORS, so these come through the backend proxy
  // (/api/v1/repository/list) rather than a direct fetch like GitHub.
  const [srcAzRepos, setSrcAzRepos] = useState([]);
  useEffect(() => {
    const a = azAcc();
    if (sourceMode !== "repository" || srcProvider !== "azure-repos" || !(a && a.azDevopsPat && srcProject.trim())) { setSrcAzRepos([]); return; }
    let live = true;
    listRepositories(backendUrl, token(), { provider: "azure-repos", azureRepos: { orgUrl: a.organization || "", pat: a.azDevopsPat || "", project: srcProject.trim() } })
      .then(({ ok, data }) => { if (live && ok && data && data.success && Array.isArray(data.repos)) setSrcAzRepos(data.repos.map((r) => r.name).filter(Boolean)); })
      .catch(() => {});
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceMode, srcProvider, sel.azure, srcProject]);

  // Branches of each selected source repo (keyed by the exact repo value in the row),
  // for the branch dropdown. Fetched from GitHub with the account token.
  const [srcBranches, setSrcBranches] = useState({});
  useEffect(() => {
    const c = ghCreds();
    if (sourceMode !== "repository" || srcProvider !== "github" || !(c && c.token)) return;
    const wanted = Array.from(new Set(srcRepos.map((r) => (r.repo || "").trim()).filter(Boolean)));
    const ctrls = [];
    wanted.forEach((repo) => {
      if (srcBranches[repo] !== undefined) return; // already fetched
      const full = repo.includes("/") ? repo : (c.owner ? `${c.owner}/${repo}` : null);
      if (!full) return;
      const ctrl = new AbortController(); ctrls.push(ctrl);
      fetch(`https://api.github.com/repos/${full}/branches?per_page=100`, {
        headers: { Authorization: `Bearer ${c.token}`, Accept: "application/vnd.github+json" }, signal: ctrl.signal,
      })
        .then((r) => (r.ok ? r.json() : []))
        .then((list) => { const names = Array.isArray(list) ? list.map((b) => b.name).filter(Boolean) : []; setSrcBranches((prev) => ({ ...prev, [repo]: names })); })
        .catch(() => {});
    });
    return () => ctrls.forEach((x) => x.abort());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceMode, srcProvider, sel.repo, JSON.stringify(srcRepos.map((r) => (r.repo || "").trim()))]);

  // Branches of each selected Azure source repo — via the backend proxy (CORS). Keyed into
  // the same srcBranches map so the row renderer is provider-agnostic.
  useEffect(() => {
    const a = azAcc();
    if (sourceMode !== "repository" || srcProvider !== "azure-repos" || !(a && a.azDevopsPat && srcProject.trim())) return;
    const wanted = Array.from(new Set(srcRepos.map((r) => (r.repo || "").trim()).filter(Boolean)));
    let live = true;
    wanted.forEach((repo) => {
      if (srcBranches[repo] !== undefined) return; // already fetched
      listRepositories(backendUrl, token(), { provider: "azure-repos", azureRepos: { orgUrl: a.organization || "", pat: a.azDevopsPat || "", project: srcProject.trim() }, repo })
        .then(({ ok, data }) => { if (live && ok && data && Array.isArray(data.branches)) setSrcBranches((prev) => ({ ...prev, [repo]: data.branches })); })
        .catch(() => {});
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceMode, srcProvider, sel.azure, srcProject, JSON.stringify(srcRepos.map((r) => (r.repo || "").trim()))]);

  const selectedProject = () => projects.find((x) => String(x._id) === selectedProjectId) || null;

  const buildSource = () => {
    if (sourceMode === "archive") return { type: "archive" };
    if (sourceMode === "path") {
      // Deploy reads the code from disk by folder path. Use "path" (a folder under
      // uploads/), NOT "generated": a "generated" source is ownership-checked against a
      // completed generation Task for THIS sessionId, and the Deployment Agent runs its
      // own fresh deploy session (no generation Task) → that path always 404s here.
      const p = selectedProject();
      let root = projectPath.trim();
      if (!root && p && Array.isArray(p.generatedApis)) {
        const paths = p.generatedApis.map((a) => a.projectPath).filter(Boolean);
        if (paths.length === 1) {
          root = paths[0];
        } else if (paths.length > 1) {
          // common parent folder of the service paths (handles / and \ separators)
          const parts = paths.map((x) => x.split(/[\\/]/));
          const min = Math.min(...parts.map((a) => a.length));
          const common = [];
          for (let i = 0; i < min; i++) {
            const seg = parts[0][i];
            if (parts.every((a) => a[i] === seg)) common.push(seg);
            else break;
          }
          root = common.join("\\");
        }
      }
      return { type: "path", projectPath: root };
    }
    // repository: deploy existing branches in place (run-branch). The GitHub dropdown holds
    // the owner-qualified full name (owner/repo); the backend wants `repo` as the bare name
    // and `owner` as a separate field (REPO_NAME_RE rejects a slash). Azure repos are bare.
    const repositories = srcRepos.filter((r) => r.repo.trim()).map((r) => {
      const raw = r.repo.trim();
      let owner; let repo = raw;
      if (srcProvider === "github" && raw.includes("/")) { const i = raw.indexOf("/"); owner = raw.slice(0, i); repo = raw.slice(i + 1); }
      const o = { repo, branch: (r.branch || "").trim() };
      if (owner) o.owner = owner;
      if (r.service && r.service.trim()) o.service = r.service.trim();
      return o;
    });
    return { type: "repository", provider: srcProvider, repositories };
  };

  const needsTargets = sourceMode === "archive" || sourceMode === "path"; // push-and-run → pick target repos

  // ── Analyze ──────────────────────────────────────────────────
  const doAnalyze = async () => {
    setError(""); setAnalyzeError(""); setUnits(null); setTargetRepos([]); setTargetValid(false);
    const source = buildSource();
    if (sourceMode === "archive" && !archiveFile) { setError("Choose a .zip / .jar or a folder."); return; }
    if (sourceMode === "path" && !(source.projectPath || (source.projectPaths && source.projectPaths.length))) { setError("Pick a project or enter a path."); return; }
    if (sourceMode === "repository" && !source.repositories.length) { setError("Add at least one repository + branch."); return; }
    const gh = ghCreds();
    const az = azAcc();
    if (sourceMode === "repository" && srcProvider === "github" && !(gh && gh.token && gh.owner)) { setError("Pick a GitHub account for the repositories."); return; }
    if (sourceMode === "repository" && srcProvider === "azure-repos" && !(az && az.azDevopsPat && srcProject.trim())) { setError("Pick an Azure DevOps account and enter a project."); return; }
    setAnalyzing(true);
    try {
      let body;
      if (sourceMode === "archive") {
        const fd = new FormData(); fd.append("sessionId", sessionId); fd.append("source", JSON.stringify(source)); fd.append("projectArchive", archiveFile);
        body = fd;
      } else {
        body = { sessionId, source };
        if (sourceMode === "repository" && srcProvider === "github") body.github = { token: gh.token, owner: gh.owner };
        if (sourceMode === "repository" && srcProvider === "azure-repos") body.azureRepos = { orgUrl: az.organization || "", pat: az.azDevopsPat || "", project: srcProject.trim() };
      }
      const { ok, data } = await analyzeProject(backendUrl, token(), body);
      if (ok && data && data.success) {
        const flat = (data.projects || []).flatMap((p) => (p.units || []).map((u) => ({ service: u.service, kind: u.kind, suggestedRepo: u.suggestedRepo })));
        setUnits(flat);
        const techFromAnalyze = (data.technology || (data.projects || [])[0]?.technology);
        if (techFromAnalyze && TECHS.some((t) => t.key === techFromAnalyze)) setTech(techFromAnalyze);
        const gen = (data.projects || [])[0]?.generatedFor;
        if (gen === "github-actions" || gen === "azure-devops") setCiPlatform(gen);
      } else {
        setAnalyzeError((data && (data.error || data.message)) || "Could not analyze the source.");
      }
    } catch (e) { setAnalyzeError(e.message); }
    finally { setAnalyzing(false); }
  };

  // ── Deploy (and resend on validation answers) ────────────────
  const sendDeploy = async (answers) => {
    const gh = ghCreds(); const az = azAcc(); const mule = muleAcc(); const kv = vaultAcc();
    // Where the code lives / is pushed: for archive/path sources it's the chosen target
    // provider; for a repository source it's the source provider (deployed in place).
    const codeProvider = needsTargets
      ? targetProvider
      : (srcProvider === "azure-repos" ? "azure-repos" : "github");
    const azProject = codeProvider === "azure-repos"
      ? (needsTargets ? targetProject.trim() : srcProject.trim())
      : (projectName.trim() || "Intelog");
    const rowsToObj = (rows) => rows.reduce((o, r) => { if (r.k && r.k.trim()) o[r.k.trim()] = r.v; return o; }, {});
    const variables = rowsToObj(valVars); const secrets = rowsToObj(valSecrets);
    const config = buildV3Config({
      tech, ciPlatform, codeProvider,
      github: gh ? { token: gh.token, owner: gh.owner } : null,
      azure: az ? { orgUrl: az.organization || "", pat: az.azDevopsPat || "", project: azProject } : null,
      repos: needsTargets && !useMono ? targetRepos : [],
      monoRepo: useMono && mono.repo.trim() ? { repo: mono.repo.trim(), branch: mono.branch.trim(), baseBranch: mono.baseBranch.trim() } : null,
      githubValues: ciPlatform === "github-actions" ? { variables, secrets } : undefined,
      azureValues: ciPlatform === "azure-devops" ? { variables, secretVariables: secrets } : undefined,
      keyVault: kv ? { enabled: true, name: kv.keyVaultName || "", subscriptionName: kv.subscriptionName || "", tenantId: kv.tenantId || "", subscriptionId: kv.subscriptionId || "", clientId: kv.serviceprincipalid || "", clientSecret: kv.serviceprincipalkey || "" } : null,
      anypoint: tech === "mule" && mule ? { enabled: true, orgName: mule.muleOrg || mule.name || "", envName: sel.env || "", clientId: mule.connAppClientId || "", clientSecret: mule.connAppClientSecret || "", nexusUsername: nexus.username, nexusPassword: nexus.password } : null,
    });
    // Merge validation answers (nexus etc.) into the config.
    if (answers && answers.configMerges) for (const m of answers.configMerges) setDeep(config, m.path, m.value);

    const source = buildSource();
    const bodyFields = deployBodyFields(tech, config, "1.0.0");
    lastRequestRef.current = { source, config, version: "1.0.0" };

    const endpoint = `${backendUrl}${deployEndpoint(tech)}`;
    const hasFiles = sourceMode === "archive" || (answers && answers.files && Object.keys(answers.files).length);
    setThinking(true); setLoading(true);
    if (phase !== "validation") { setLogs([]); setStages(seedStages(tech, ciPlatform)); setPipelineStages([]); setConfirmation(null); }
    try {
      let res;
      if (hasFiles) {
        const fd = new FormData();
        fd.append("sessionId", sessionId);
        fd.append("source", JSON.stringify(source));
        for (const [k, v] of Object.entries(bodyFields)) fd.append(k, JSON.stringify(v));
        if (sourceMode === "archive" && archiveFile) fd.append("projectArchive", archiveFile);
        if (answers) {
          if (answers.pipelines && answers.pipelines.length) fd.append("pipelines", JSON.stringify(answers.pipelines));
          if (answers.pipelineUploads && answers.pipelineUploads.length) fd.append("pipelineUploads", JSON.stringify(answers.pipelineUploads));
          if (answers.skip && answers.skip.length) fd.append("skip", JSON.stringify(answers.skip));
          if (answers.files) for (const [field, file] of Object.entries(answers.files)) fd.append(field, file);
        }
        res = await fetch(endpoint, { method: "POST", headers: { "x-access-token": token() }, body: fd });
      } else {
        const jsonBody = { sessionId, source, ...bodyFields };
        if (sourceMode === "repository" && srcProvider === "github" && ghCreds()) jsonBody.github = { ...(jsonBody.config && jsonBody.config.github), token: ghCreds().token, owner: ghCreds().owner };
        if (sourceMode === "repository" && srcProvider === "azure-repos" && azAcc()) jsonBody.azureRepos = { orgUrl: azAcc().organization || "", pat: azAcc().azDevopsPat || "", project: srcProject.trim() };
        if (answers) {
          if (answers.pipelines && answers.pipelines.length) jsonBody.pipelines = answers.pipelines;
          if (answers.skip && answers.skip.length) jsonBody.skip = answers.skip;
        }
        res = await fetch(endpoint, { method: "POST", headers: { "Content-Type": "application/json", "x-access-token": token() }, body: JSON.stringify(jsonBody) });
      }
      const j = await res.json().catch(() => ({ success: false, error: `HTTP ${res.status}` }));
      routeResponse(j);
    } catch (e) {
      setThinking(false); setStarted(false); setError(e.message);
      pushBot(`I couldn't reach the deployment service: ${e.message}. Check your setup and try again.`);
    } finally { setLoading(false); }
  };

  const startDeploy = async () => {
    setError("");
    const targetsOk = useMono ? !!mono.repo.trim() : targetValid;
    if (needsTargets && !targetsOk) { setError(useMono ? "Enter the repository for the project." : "Finish choosing a repository and branch for every service."); return; }
    setStarted(true);
    const what = sourceMode === "archive" ? (archiveFile && archiveFile.name) : sourceMode === "path" ? (projectName.trim() || projectPath.trim()) : `${(srcRepos[0] || {}).repo || "repositories"}`;
    pushUser(`Deploy ${what || "my project"} (${tech === "springboot" ? "Spring Boot" : tech === "dotnet" ? ".NET" : "Mule"})`);
    pushBot("Validating and deploying…");
    await sendDeploy(null);
  };

  // Authoritative stage statuses from the final structured result — the push (repo) stage
  // and the CI-run stage are judged separately, so a successful push + failed CI run shows
  // the push stage done and only the CI stage failed (not the whole strip red).
  const setStagesFromResult = (j) => {
    const units = (j.units && j.units.length ? j.units : j.apis) || [];
    if (!units.length) return;
    const runFailed = units.some((u) => {
      const runs = u.runs || (u.run ? [u.run] : []);
      const vals = runs.map((r) => String(r.conclusion || r.result || r.status || ""));
      if (u.run && u.run.result) vals.push(String(u.run.result));
      return vals.some((v) => /fail|cancel|error|timed|timeout/i.test(v));
    });
    const pushOk = units.every((u) => u.repository || u.commit || u.pushed || /creat|updat|push/i.test(String(u.action || "")));
    setStages((prev) => prev.map((s) => {
      if (isCiStage(s.name)) return { ...s, status: runFailed ? "error" : "done" };
      if (isPushStage(s.name)) return { ...s, status: pushOk ? "done" : "error" };
      return { ...s, status: s.status === "error" ? "error" : "done" };
    }));
  };

  const routeResponse = (j) => {
    setThinking(false);
    if (!j) { pushBot("No response from the server. Please try again."); return; }
    if (j.sessionId) { setSessionId(j.sessionId); try { socketRef.current && socketRef.current.emit("join-session", j.sessionId); } catch (_) {} }
    if (j.type === "deployment_validation_pending") {
      const n = (j.missingItems || []).length;
      pushBot(`I need ${n} thing${n === 1 ? "" : "s"} before deploying — fill ${n === 1 ? "it" : "them"} in below and I'll continue.`);
      setValidation(j); setPhase("validation"); return;
    }
    const hasUnits = (j.units && j.units.length) || (j.apis && j.apis.length);
    if (j.success === false && !hasUnits) {
      // Top-level failure (no per-service results).
      if (/no pending deployment|expired/i.test(j.error || "")) {
        setPhase("setup"); setValidation(null); setStarted(false); setError(j.error);
        pushBot("That deployment session expired — let's set it up again."); return;
      }
      const code = j.code;
      const msg = (code && DEPLOY_ERROR_MESSAGES[code]) || j.error || "Something went wrong.";
      if (code === "BRANCH_MOVED" || code === "BRANCH_CONFLICT") { setRetryable(true); }
      pushBot(msg);
      setLogs((prev) => [...prev, { type: "error", timestamp: nowTime(), text: msg }]);
      // A top-level failure (no per-service result) must fail the active stage so the strip
      // doesn't hang on "working…".
      setStages((prev) => prev.map((s) => (s.status === "active" ? { ...s, status: "error" } : s)));
      return;
    }
    setValidation(null); setRetryable(false); setPhase("deploying");
    pushBot(j.success === false ? "Deployment finished with some errors — see the result below." : "All set — deploying now 🚀 Watch the progress below.");
    if (j.message) setLogs((prev) => [...prev, { type: "success", timestamp: nowTime(), text: j.message }]);
    setResult(j);
    setStagesFromResult(j);
    renderResult(j);
  };

  const renderResult = (j) => {
    const units = j.units || j.apis || [];
    for (const u of units) {
      const name = u.repository || u.name || u.service;
      const run = (u.runs && u.runs[0]) || u.run || u.pipeline;
      const where = u.branch ? `@${u.branch}` : "";
      // On a failed unit show the real reason (error/message, or the run's conclusion/result)
      // instead of the generic "deployed", so the result card explains WHY it failed.
      const outcome = u.success === false
        ? (u.error || u.message || (run && (run.conclusion || run.result)) || "failed")
        : (u.action || "deployed");
      setLogs((prev) => [...prev, { type: u.success === false ? "error" : "success", timestamp: nowTime(), text: `${name} ${where} — ${outcome}${run && run.url ? ` · ${run.url}` : ""}` }]);
    }
    for (const w of (j.warnings || [])) setLogs((prev) => [...prev, { type: "info", timestamp: nowTime(), text: `⚠ ${typeof w === "string" ? w : (w.message || w.type)}` }]);
  };

  const onProceed = () => { try { socketRef.current && socketRef.current.emit("deployment:user_confirmed", { phase: confirmation && confirmation.phase, sessionId }); } catch (_) {} setConfirmation(null); };
  const onCancel = () => { try { socketRef.current && socketRef.current.emit("deployment:cancel", { sessionId }); } catch (_) {} setConfirmation(null); };

  const resetAll = () => {
    setSessionId(genSessionId()); setPhase("setup"); setValidation(null);
    setArchiveFile(null); setZipInfo(null); setUnits(null); setTargetRepos([]); setTargetValid(false);
    setLogs([]); setStages([]); setPipelineStages([]); setConfirmation(null); setError(""); setThinking(false); setStarted(false);
    setResult(null); setRetryable(false);
    setMessages([{ id: `b${Date.now()}`, role: "bot", text: GREETING }]);
  };

  return (
    <div className="root">
      <CustomHeader />
      <main className="content">
        <div className="da-page">
          <PageHeader
            className="da-page-header"
            title="Deployment Agent"
            subtitle="Analyze a source, choose each service's repository + branch, and run the pipeline — GitHub Actions or Azure Pipelines."
            actions={phase !== "setup" ? <button className="da-reset" onClick={resetAll}>↺ New deployment</button> : null}
          />

          <div className="da-chat da-scroll">
            {messages.map((m) => (
              <div key={m.id} className={`da-chat-msg ${m.role}`}>
                {m.role === "bot" && <div className="da-chat-avatar"><img src={whiteLogo} alt="agent" /></div>}
                <div className={`da-chat-bubble ${m.role}`}>{m.text}</div>
              </div>
            ))}
            {thinking && (
              <div className="da-chat-msg bot"><div className="da-chat-avatar"><img src={whiteLogo} alt="agent" /></div><div className="da-chat-bubble bot da-chat-typing"><span /><span /><span /></div></div>
            )}

            {!started && (
              <section className="da-card">
                <div className="da-row">
                  <label className="da-field"><span>Technology</span>
                    <select className="da-input" value={tech} onChange={(e) => setTech(e.target.value)}>{TECHS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}</select>
                  </label>
                  <label className="da-field"><span>CI platform</span>
                    <select className="da-input" value={ciPlatform} onChange={(e) => setCiPlatform(e.target.value)}>
                      <option value="github-actions">GitHub Actions</option>
                      <option value="azure-devops">Azure Pipelines</option>
                    </select>
                  </label>
                </div>

                <div className="da-mode">
                  <button className={`da-tab ${sourceMode === "archive" ? "active" : ""}`} onClick={() => { setSourceMode("archive"); setUnits(null); }}>📦 Upload archive</button>
                  <button className={`da-tab ${sourceMode === "path" ? "active" : ""}`} onClick={() => { setSourceMode("path"); setUnits(null); }}>📁 Intelog Project Centre</button>
                  <button className={`da-tab ${sourceMode === "repository" ? "active" : ""}`} onClick={() => { setSourceMode("repository"); setUnits(null); }}>🔗 Repositories (branch)</button>
                </div>

                {sourceMode === "archive" && (
                  <div className="da-row">
                    <label className="da-drop" style={{ flex: 1, minWidth: 220, margin: 0 }}>
                      <input type="file" accept=".zip,.jar" style={{ display: "none" }} onChange={(e) => { setZipInfo(null); setArchiveFile(e.target.files && e.target.files[0]); setUnits(null); }} />
                      {archiveFile && !zipInfo ? <span>📦 {archiveFile.name}</span> : <span>📦 Drop or click — <strong>.zip</strong> / <strong>.jar</strong></span>}
                    </label>
                    <label className="da-drop" style={{ flex: 1, minWidth: 220, margin: 0 }}>
                      <input type="file" webkitdirectory="" directory="" style={{ display: "none" }} disabled={zipping} onChange={(e) => { zipLocalFolder(e.target.files); setUnits(null); }} />
                      {zipping ? <span style={{ color: "#f5a623" }}>Zipping folder…</span> : zipInfo ? <span style={{ color: "#00c98d", fontWeight: 600 }}>✓ 📁 {zipInfo.name} ({zipInfo.count} files)</span> : <span>📁 Choose a <strong>project folder</strong></span>}
                    </label>
                  </div>
                )}

                {sourceMode === "path" && (
                  <>
                    <label className="da-field"><span>Project (from Project Centre)</span>
                      <select className="da-input" value={selectedProjectId} onChange={(e) => { const id = e.target.value; setSelectedProjectId(id); const p = projects.find((x) => String(x._id) === id); if (p) { setProjectPath(p.projectPath || ""); const t = techOf(p); if (t) setTech(t); if (p.projectName) setProjectName(p.projectName); } setUnits(null); }}>
                        <option value="">{projects.length ? "— select a project —" : "— no saved projects —"}</option>
                        {projects.map((p) => <option key={p._id} value={p._id}>{p.projectName || "(unnamed)"}{p.technology || p.projectType ? ` — ${p.technology || p.projectType}` : ""}</option>)}
                      </select>
                    </label>
                    <label className="da-field"><span>Project path (on server)</span>
                      <input className="da-input" value={projectPath} onChange={(e) => { setProjectPath(e.target.value); setSelectedProjectId(""); setUnits(null); }} placeholder="Pick a project above, or enter a path" />
                    </label>
                  </>
                )}

                {sourceMode === "repository" && (
                  <>
                    <div className="da-mode" style={{ marginBottom: 8 }}>
                      <button className={`da-tab ${srcProvider === "github" ? "active" : ""}`} onClick={() => { setSrcProvider("github"); setUnits(null); }}>GitHub</button>
                      <button className={`da-tab ${srcProvider === "azure-repos" ? "active" : ""}`} onClick={() => { setSrcProvider("azure-repos"); setUnits(null); }}>Azure Repos</button>
                    </div>
                    {srcProvider === "github" ? (
                      <Dropdown label="GitHub account (owner / token)" list={repoAccounts} value={sel.repo} onChange={(v) => setSel((s) => ({ ...s, repo: v }))} render={(a) => a.gitUsername || a.username || a.name || "GitHub"} />
                    ) : (
                      <div className="da-row">
                        <Dropdown label="Azure DevOps account" list={azureAccounts} value={sel.azure} onChange={(v) => setSel((s) => ({ ...s, azure: v }))} render={(a) => a.organization || a.azusername || a.name || "Azure"} />
                        <label className="da-field"><span>Azure DevOps project</span><input className="da-input" value={srcProject} onChange={(e) => { setSrcProject(e.target.value); setUnits(null); }} placeholder="e.g. Shop" /></label>
                      </div>
                    )}
                    {srcProvider === "azure-repos" && azAcc() && !srcProject.trim() && (
                      <div style={{ fontSize: 11.5, color: "#f5a623", margin: "-2px 0 6px" }}>Enter the Azure DevOps project above to list its repositories and branches.</div>
                    )}
                    <div className="da-field"><span>Repositories + branches (deployed in place)</span></div>
                    {srcRepos.map((r, i) => {
                      const upd = (patch) => setSrcRepos((p) => p.map((x, j) => (j === i ? { ...x, ...patch } : x)));
                      const srcRepoList = srcProvider === "azure-repos" ? srcAzRepos : srcGhRepos;
                      const hasList = srcRepoList.length > 0;
                      const manual = !!r.manual || (!!(r.repo || "").trim() && hasList && !srcRepoList.includes(r.repo));
                      return (
                        <div className="da-row" key={i} style={{ marginBottom: 6 }}>
                          <input className="da-input" placeholder="service (optional)" value={r.service || ""} onChange={(e) => { const val = e.target.value; upd({ service: val }); }} />
                          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                            {hasList && !manual ? (
                              <select className="da-input" value={srcRepoList.includes(r.repo) ? r.repo : ""} onChange={(e) => { const v = e.target.value; if (v === "__manual__") upd({ manual: true }); else { upd({ manual: false, repo: v }); setUnits(null); } }}>
                                <option value="">— select a repository —</option>
                                {srcRepoList.map((n) => <option key={n} value={n}>{n}</option>)}
                                <option value="__manual__">✎ Enter manually…</option>
                              </select>
                            ) : (
                              <input className="da-input" placeholder="owner/repo or repo" value={r.repo} onChange={(e) => { const val = e.target.value; upd({ repo: val, manual: hasList ? true : undefined }); setUnits(null); }} />
                            )}
                            {hasList && manual && <button type="button" onClick={() => { upd({ manual: false, repo: "" }); }} style={{ alignSelf: "flex-start", background: "none", border: "none", color: "#3b7fff", fontSize: 10.5, cursor: "pointer", padding: 0 }}>↩ pick from list</button>}
                          </div>
                          {(() => {
                            const brList = srcBranches[(r.repo || "").trim()] || null;
                            const brManual = !!r.branchManual || (!!(r.branch || "").trim() && brList && brList.length > 0 && !brList.includes(r.branch));
                            return (
                              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                                {brList && brList.length > 0 && !brManual ? (
                                  <select className="da-input" value={brList.includes(r.branch) ? r.branch : ""} onChange={(e) => { const v = e.target.value; if (v === "__manual__") upd({ branchManual: true }); else upd({ branchManual: false, branch: v }); }}>
                                    <option value="">— select a branch —</option>
                                    {brList.map((b) => <option key={b} value={b}>{b}</option>)}
                                    <option value="__manual__">✎ Enter manually…</option>
                                  </select>
                                ) : (
                                  <input className="da-input" placeholder="branch (e.g. main)" value={r.branch} onChange={(e) => { const val = e.target.value; upd({ branch: val, branchManual: brList && brList.length ? true : undefined }); }} />
                                )}
                                {brList && brList.length > 0 && brManual && <button type="button" onClick={() => { upd({ branchManual: false, branch: "" }); }} style={{ alignSelf: "flex-start", background: "none", border: "none", color: "#3b7fff", fontSize: 10.5, cursor: "pointer", padding: 0 }}>↩ pick from list</button>}
                              </div>
                            );
                          })()}
                          {srcRepos.length > 1 && <button className="da-tab" onClick={() => setSrcRepos((p) => p.filter((_, j) => j !== i))}>✕</button>}
                        </div>
                      );
                    })}
                    <button className="da-tab" onClick={() => setSrcRepos((p) => [...p, { service: "", repo: "", branch: "" }])}>+ Add repository</button>
                  </>
                )}

                <div style={{ marginTop: 12 }}>
                  <button className="da-deploy" onClick={doAnalyze} disabled={analyzing} style={{ marginRight: 8 }}>{analyzing ? "Analyzing…" : "🔍 Analyze"}</button>
                </div>
                {analyzeError && <div className="da-error">{analyzeError}</div>}
                {!units && needsTargets && !analyzing && (
                  <div style={{ marginTop: 10, padding: "10px 12px", border: "1px dashed rgba(59,127,255,0.4)", borderRadius: 8, fontSize: 12, color: "#aac0dc", background: "rgba(59,127,255,0.05)" }}>
                    🔍 Click <strong>Analyze</strong> to list the services — then you'll choose each service's <strong>target repository &amp; branch</strong> here.
                  </div>
                )}

                {units && needsTargets && (
                  <div className="da-creds" style={{ marginTop: 12 }}>
                    <div className="da-creds-title">Target repositories</div>
                    <div className="da-mode" style={{ marginBottom: 8 }}>
                      <button className={`da-tab ${targetProvider === "github" ? "active" : ""}`} onClick={() => { setTargetProvider("github"); setTargetRepos([]); setTargetValid(false); }}>GitHub</button>
                      <button className={`da-tab ${targetProvider === "azure-repos" ? "active" : ""}`} onClick={() => { setTargetProvider("azure-repos"); setTargetRepos([]); setTargetValid(false); }}>Azure Repos</button>
                    </div>
                    {targetProvider === "github" ? (
                      <Dropdown label="GitHub account (push target)" list={repoAccounts} value={sel.repo} onChange={(v) => setSel((s) => ({ ...s, repo: v }))} render={(a) => a.gitUsername || a.username || a.name || "GitHub"} />
                    ) : (
                      <div className="da-row">
                        <Dropdown label="Azure DevOps account (push target)" list={azureAccounts} value={sel.azure} onChange={(v) => setSel((s) => ({ ...s, azure: v }))} render={(a) => a.organization || a.azusername || a.name || "Azure"} />
                        <label className="da-field"><span>Azure DevOps project</span><input className="da-input" value={targetProject} onChange={(e) => setTargetProject(e.target.value)} placeholder="e.g. Shop" /></label>
                      </div>
                    )}
                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#aac0dc", margin: "4px 0 8px" }}>
                      <input type="checkbox" checked={useMono} onChange={(e) => setUseMono(e.target.checked)} /> Use one repository for the whole project
                    </label>
                    {useMono ? (
                      <div className="da-row">
                        <input className="da-input" placeholder={targetProvider === "github" ? "owner/repo or repo" : "repository name"} value={mono.repo} onChange={(e) => { const val = e.target.value; setMono((m) => ({ ...m, repo: val })); }} />
                        <input className="da-input" placeholder="branch (e.g. main)" value={mono.branch} onChange={(e) => { const val = e.target.value; setMono((m) => ({ ...m, branch: val })); }} />
                        <input className="da-input" placeholder="base branch (optional)" value={mono.baseBranch} onChange={(e) => { const val = e.target.value; setMono((m) => ({ ...m, baseBranch: val })); }} />
                      </div>
                    ) : targetProvider === "github" ? (
                      ghCreds() ? (
                        <RepoBranchRows provider="github" sessionId={sessionId} backendUrl={backendUrl} token={token()} creds={ghCreds()} units={units} projName={projectName || (units[0] && units[0].service)} onRepos={(r, v) => { setTargetRepos(r); setTargetValid(v); }} />
                      ) : <div style={{ fontSize: 12, color: "#f5a623" }}>Pick a GitHub account to choose target repositories.</div>
                    ) : (
                      (azAcc() && targetProject.trim()) ? (
                        <RepoBranchRows provider="azure-repos" sessionId={sessionId} backendUrl={backendUrl} token={token()} creds={{ orgUrl: azAcc().organization || "", pat: azAcc().azDevopsPat || "", project: targetProject.trim() }} units={units} projName={projectName || (units[0] && units[0].service)} onRepos={(r, v) => { setTargetRepos(r); setTargetValid(v); }} />
                      ) : <div style={{ fontSize: 12, color: "#f5a623" }}>Pick an Azure DevOps account and enter a project to choose target repositories.</div>
                    )}
                  </div>
                )}
                {units && !needsTargets && <div style={{ fontSize: 12, color: "#00c98d", marginTop: 8 }}>{units.length} service(s) found — will deploy the branches in place.</div>}

                <div className="da-creds" style={{ marginTop: 12 }}>
                  <div className="da-creds-title">Credentials (from Connected Apps)</div>
                  <div className="da-row">
                    {ciPlatform === "azure-devops" && <Dropdown label="Azure DevOps account" list={azureAccounts} value={sel.azure} onChange={(v) => setSel((s) => ({ ...s, azure: v }))} render={(a) => a.organization || a.azusername || a.name || "Azure"} />}
                    {(tech === "springboot" || tech === "dotnet") && <Dropdown label="Key Vault (optional)" list={vaultAccounts} value={sel.vault} onChange={(v) => setSel((s) => ({ ...s, vault: v }))} render={(a) => a.keyVaultName || a.subscriptionName || a.name || "Vault"} />}
                  </div>
                  {tech === "mule" && (
                    <>
                      <div className="da-row">
                        <Dropdown label="Anypoint account" list={muleAccounts} value={sel.mule} onChange={(v) => { setSel((s) => ({ ...s, mule: v, env: "" })); const a = v === "" ? null : muleAccounts[Number(v)]; fetchEnvironments(a); setNexus({ username: (a && a.eeMavenUsername) || "", password: (a && a.eeMavenPassword) || "" }); }} render={(a) => a.name || a.muleOrg || a.connAppClientId || "Anypoint"} />
                        <label className="da-field"><span>Environment</span>
                          <select className="da-input" value={sel.env} disabled={loadingEnv || sel.mule === ""} onChange={(e) => { const val = e.target.value; setSel((s) => ({ ...s, env: val })); }}>
                            <option value="">{sel.mule === "" ? "— account first —" : loadingEnv ? "Loading…" : environments.length ? "— select —" : "— none —"}</option>
                            {environments.map((env) => <option key={env.id || env.name} value={env.name}>{env.name}{env.isProduction ? " (prod)" : ""}</option>)}
                          </select>
                        </label>
                      </div>
                      <div style={{ fontSize: 11, color: "#7a93bc", margin: "8px 0 2px" }}>
                        MuleSoft EE Maven credentials (Nexus) — loaded from the selected Anypoint account in Connected Apps (add them there). You can override them here for this deploy. Required for Azure Pipelines; optional for GitHub Actions.
                      </div>
                      <div className="da-row">
                        <label className="da-field"><span>MuleSoft EE Maven username</span><input className="da-input" value={nexus.username} onChange={(e) => { const val = e.target.value; setNexus((n) => ({ ...n, username: val })); }} /></label>
                        <label className="da-field"><span>MuleSoft EE Maven password</span><input className="da-input" type="password" value={nexus.password} onChange={(e) => { const val = e.target.value; setNexus((n) => ({ ...n, password: val })); }} /></label>
                      </div>
                    </>
                  )}
                </div>

                <div className="da-creds" style={{ marginTop: 12 }}>
                  <div className="da-creds-title" style={{ cursor: "pointer" }} onClick={() => setShowValues((v) => !v)}>
                    {showValues ? "▾" : "▸"} Pipeline values (optional)
                  </div>
                  {showValues && (
                    <>
                      <KeyValRows label={ciPlatform === "azure-devops" ? "Variables (azure.variables)" : "Variables (github.variables)"} rows={valVars} setRows={setValVars} />
                      <KeyValRows label={ciPlatform === "azure-devops" ? "Secret variables (azure.secretVariables)" : "Secrets (github.secrets)"} rows={valSecrets} setRows={setValSecrets} secret />
                    </>
                  )}
                </div>

                {error && <div className="da-error">{error}</div>}
                <button className="da-deploy" onClick={startDeploy} disabled={loading || !units}>{loading ? "Submitting…" : "🚀 Deploy"}</button>
              </section>
            )}

            {phase === "validation" && validation && (
              <section className="da-card">
                <DeploymentValidationPanel data={validation} disabled={loading} onAnswers={(answers) => { pushUser("Submitted the requested details."); sendDeploy(answers); }} />
              </section>
            )}

            {started && (phase === "deploying" || logs.length > 0 || confirmation) && (
              <DeploymentAgentConsole tech={tech} stages={stages} pipelineStages={pipelineStages} logs={logs} confirmation={confirmation} onProceed={onProceed} onCancel={onCancel} onReset={resetAll} />
            )}
            {result && <DeploymentResult data={result} />}
            {retryable && (
              <button className="da-deploy" style={{ marginTop: 10 }} disabled={loading} onClick={() => { setRetryable(false); pushUser("Deploy again"); pushBot("Deploying again…"); sendDeploy(null); }}>
                {loading ? "Submitting…" : "↻ Deploy again"}
              </button>
            )}
            <div ref={chatEndRef} />
          </div>
        </div>
      </main>
    </div>
  );
}
